import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Tooltip, Typography } from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useRef, useState } from "react";
import s from "./SwingTable.module.css";
import { HelpOutlineOutlined } from "@material-ui/icons";
import { ironsClub, woodsClub } from "../../SwingLibrary";
import { Pagination } from "./Pagination";
import {
  renderUnitType,
  renderZScore,
  summarizeIndicatorsByClub,
} from "./utils";

const SortableHeader = ({
  header,
  handleSort,
  renderSortArrow,
  columnIndex,
  onHover,
  tableNavbarHeight,
}) => {
  const {
    setNodeRef,
    transform,
    transition,
    attributes,
    listeners,
    isDragging,
  } = useSortable({
    id: header.id,
    disabled: columnIndex === 0,
  });

  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isResized, setIsResized] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [header.column.columnDef.header]);

  const colWidth = {
    videoPath: {
      width: "150px",
    },
    "userData.golfClub": {
      width: "34px",
    },
    "userData.fullName": {
      minWidth: "72px",
      maxWidth: "141px",
    },
    tags: {
      minWidth: "34px",
      maxWidth: "141px",
    },
  };

  const style = {
    opacity: isDragging ? 0.8 : 1,
    transform: CSS.Translate.toString(transform),
    transition: "width transform 0.2s ease-in-out",
    whiteSpace: "nowrap",
    width:
      colWidth[header.column.columnDef.accessorKey]?.width && !isResized
        ? colWidth[header.column.columnDef.accessorKey]?.width
        : header.column.getSize(),
    maxWidth:
      colWidth[header.column.columnDef.accessorKey]?.maxWidth && !isResized
        ? colWidth[header.column.columnDef.accessorKey]?.maxWidth
        : "",
    minWidth:
      colWidth[header.column.columnDef.accessorKey]?.minWidth && !isResized
        ? colWidth[header.column.columnDef.accessorKey]?.minWidth
        : "",
    zIndex: isDragging ? 10 : 5,
    fontSize: "12px",
    top: tableNavbarHeight,
  };

  return (
    <th ref={setNodeRef} style={style} className={`${s.stickyHeader} `}>
      {!header.isPlaceholder && (
        <div
          style={{
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
            height: "100%",
            maxHeight: "33px",
            overflow: "hidden",
          }}
        >
          {![0, 1, 2, 3].includes(columnIndex) && (
            <span
              {...attributes}
              {...listeners}
              style={{ cursor: "grab", minWidth: "27px", width: "27px" }}
            >
              <DragIndicatorIcon />
            </span>
          )}
          <Tooltip
            placement="top"
            arrow
            title={
              isOverflowing ? (
                <Typography style={{ fontSize: 14, color: "white" }}>
                  {header.column.columnDef.header}
                </Typography>
              ) : (
                ""
              )
            }
          >
            <span
              onClick={() => handleSort(header.column.columnDef.accessorKey)}
              className={s.sortHeaderContainer}
              onMouseEnter={() => onHover(header.column.columnDef.accessorKey)}
              onMouseLeave={() => onHover(null)}
            >
              <span
                ref={textRef}
                style={{
                  display: "-webkit-box",
                  maxWidth:
                    header.column.columnDef.id === "videoPath"
                      ? "auto"
                      : "64px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  whiteSpace: "normal",
                }}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </span>
              {renderSortArrow(header.column.columnDef.accessorKey)}
            </span>
          </Tooltip>

          <div
            {...{
              onDoubleClick: () => header.column.resetSize(),
              onMouseDown: (e) => {
                setIsResized(true);
                header.getResizeHandler()(e);
              },
              onTouchStart: (e) => {
                setIsResized(true);
                header.getResizeHandler()(e);
              },
              className: `${s.resizer} ${s.isHeader} ${
                header.column.getIsResizing() && s.isResizing
              }`,
            }}
          />
        </div>
      )}
    </th>
  );
};

export const SwingTable2 = ({
  data,
  currentPage,
  onPageChange,
  pageNumber,
  onSort,
  sort,
  columns,
  rowSelection,
  setRowSelection,
  selectedTableData,
  rowsPerPage,
  onRowsPerPageChange,
  filterValueIndicators,
  isTourAveragesActive,
  databaseReference,
  tableNavbarHeight,
  filterValueClub,
}) => {
  const [columnOrder, setColumnOrder] = useState(columns.map((col) => col.id));
  const [potentialSortArrow, setPotentialSortArrow] = useState(null);

  const tableContainerRef = useRef(null);
  const isTableContainerDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleTableContainerMouseDown = (e) => {
    isTableContainerDragging.current = true;
    startX.current = e.pageX - tableContainerRef.current.offsetLeft;
    scrollLeft.current = tableContainerRef.current.scrollLeft;
  };

  const handleTableContainerMouseMove = (e) => {
    if (!isTableContainerDragging.current) return; // Only move if mouse is pressed
    e.preventDefault(); // Prevent default action (like text selection)
    const x = e.pageX - tableContainerRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5; // Adjust the scroll speed
    tableContainerRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handleTableContainerMouseUp = () => {
    isTableContainerDragging.current = false;
  };

  useEffect(() => {
    setColumnOrder(columns.map((col) => col.id));
  }, [columns]);

  const table = useReactTable({
    state: {
      rowSelection,
      columnOrder,
    },
    data: data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    columnResizeMode: "onChange",
    columnResizeDirection: "ltr",
  });

  const handleSort = (columnAccessorKey) => {
    let currentSortField = sort?.sort;
    const currentSortDirection = sort?.order;

    if (sort.sort === "videoCreated") {
      currentSortField = "videoPath";
    }

    const newDirection =
      currentSortField === columnAccessorKey
        ? currentSortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";
    onSort(columnAccessorKey, newDirection);
  };

  const renderSortArrow = (accessorKey) => {
    let currentSortField = potentialSortArrow ? potentialSortArrow : sort?.sort;

    if (sort.sort === "videoCreated" || potentialSortArrow === "videoCreated") {
      currentSortField = "videoPath";
    }

    if (currentSortField === accessorKey) {
      if (potentialSortArrow) {
        if (sort.order === "desc") {
          return <ArrowDropUpIcon />;
        } else {
          return <ArrowDropDownIcon />;
        }
      } else {
        if (sort.order === "desc") {
          return <ArrowDropDownIcon />;
        } else {
          return <ArrowDropUpIcon />;
        }
      }
    } else {
      return "";
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (
      active.id !== over?.id &&
      active.id !== columnOrder[0] &&
      over?.id !== columnOrder[0] &&
      active.id !== columnOrder[1] &&
      over?.id !== columnOrder[1] &&
      active.id !== columnOrder[2] &&
      over?.id !== columnOrder[2] &&
      active.id !== columnOrder[3] &&
      over?.id !== columnOrder[3]
    ) {
      setColumnOrder((prevOrder) => {
        const oldIndex = prevOrder.indexOf(active.id);
        const newIndex = prevOrder.indexOf(over.id);
        return arrayMove(prevOrder, oldIndex, newIndex);
      });
    }
  };

  const renderAvgAndStdV3 = (type) => {
    const summerizedData = summarizeIndicatorsByClub(
      data,
      selectedTableData,
      databaseReference
    );
    const orderedColumns = columnOrder
      .map((colId) => {
        const col = columns.find((col) => col.id === colId);
        const indicator = filterValueIndicators.find((col) => col.id === colId);
        if (indicator) {
          return { ...col, unit: indicator.unit };
        }
        return col;
      })
      .filter(Boolean);

    function getColor(deviationFromTourAvg, stdDeviation) {
      const absDeviation = Math.abs(deviationFromTourAvg);
      const absStdDev = Math.abs(stdDeviation);

      if (absDeviation < absStdDev) {
        return "#027A48";
      } else if (absDeviation >= absStdDev && absDeviation < 1.5 * absStdDev) {
        return "#FF692E";
      } else {
        return "#D92D20";
      }
    }

    if (type === "MI") {
      return orderedColumns
        .slice(4)
        .map((el) => {
          const results = summerizedData.Iron[el.id];
          return {
            avg: results?.avg ?? null,
            zScore: results?.zScore ?? null,
            tourAvg: results?.tourAvg ?? null,
            unit: el.unit,
          };
        })
        .map((el) => {
          return (
            <td className={`${s.tableStdAndAvg} ${s.tableAvg} `}>
              <div className={s.stdAvgItemConatiner}>
                <div
                  style={{
                    color:
                      isTourAveragesActive && el.avg && el.tourAvg && el.std
                        ? getColor(el.tourAvg, el.std)
                        : "black",
                  }}
                >
                  {renderUnitType(el.unit, el.avg || "n/a")}
                </div>
                <div>{renderZScore(el.unit, el.zScore ?? "n/a")}</div>

                {isTourAveragesActive ? (
                  <div>{renderZScore(el.unit, el.tourAvg ?? "n/a")}</div>
                ) : (
                  ""
                )}
              </div>
            </td>
          );
        });
    } else if (type === "D") {
      return orderedColumns
        .slice(4)
        .map((el) => {
          const results = summerizedData.Driver[el.id];
          return {
            avg: results?.avg ?? null,
            zScore: results?.zScore ?? null,
            tourAvg: results?.tourAvg ?? null,
            unit: el.unit,
          };
        })
        .map((el) => {
          return (
            <td className={`${s.tableStdAndAvg} ${s.tableStd} `}>
              <div className={s.stdAvgItemConatiner}>
                <div
                  style={{
                    color: "black",
                  }}
                >
                  {renderUnitType(el.unit, el.avg || "n/a")}
                </div>
                <div>{renderZScore(el.unit, el.zScore ?? "n/a")}</div>

                {isTourAveragesActive ? (
                  <div>{renderZScore(el.unit, el.tourAvg ?? "n/a")}</div>
                ) : (
                  ""
                )}
              </div>
            </td>
          );
        });
    }
  };

  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const avgRowRef = useRef(null);
  const [avgRowHeight, setAvgRowHeight] = useState(0);

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.getBoundingClientRect().height);
    }
    if (avgRowRef.current) {
      setAvgRowHeight(avgRowRef.current.getBoundingClientRect().height);
    }
  }, [
    headerRef.current,
    avgRowRef.current,
    rowSelection,
    isTourAveragesActive,
    columns,
    columnOrder,
  ]);

  const stdRowTop = headerHeight + avgRowHeight;

  return (
    <div>
      <div ref={tableContainerRef}>
        <table style={{ tableLayout: "fixed", width: "100%" }}>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <thead ref={headerRef}>
              <SortableContext
                items={table
                  .getHeaderGroups()[0]
                  .headers.map((header) => header.id)}
                strategy={verticalListSortingStrategy}
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <SortableHeader
                        columnIndex={index}
                        handleSort={handleSort}
                        header={header}
                        key={header.id}
                        onHover={setPotentialSortArrow}
                        renderSortArrow={renderSortArrow}
                        tableNavbarHeight={tableNavbarHeight}
                      />
                    ))}
                  </tr>
                ))}
              </SortableContext>
            </thead>
          </DndContext>

          <tbody style={{ fontSize: "14px" }}>
            {(!filterValueClub.length ||
              filterValueClub.some((club) => ironsClub.includes(club))) && (
              <tr
                ref={avgRowRef}
                className={`${s.stickyAvg}`}
                style={{ top: `${tableNavbarHeight + headerHeight - 5}px` }}
              >
                <td className={`${s.tableStdAndAvg} ${s.tableAvg}`} colSpan={4}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      style={{
                        fontFamily: "Manrope",
                        fontSize: "14px",
                        fontWeight: "700",
                        color: "#0E1829",
                      }}
                    >
                      Average (Irons)
                    </Typography>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <Typography
                        style={{
                          fontFamily: "Manrope",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#0E1829",
                        }}
                      >
                        Z Score
                      </Typography>

                      <Tooltip
                        title={
                          <Typography style={{ fontSize: 14, color: "white" }}>
                            The Z-Score measures how many standard deviations
                            this swing is away from your average
                          </Typography>
                        }
                      >
                        <HelpOutlineOutlined
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                            color: "#96A2B5",
                          }}
                        />
                      </Tooltip>
                    </div>
                    {isTourAveragesActive && (
                      <Typography
                        style={{
                          fontFamily: "Manrope",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#445568",
                        }}
                      >
                        Deviation from Tour Avg
                      </Typography>
                    )}
                  </div>
                </td>
                {renderAvgAndStdV3("MI")}
              </tr>
            )}

            {(!filterValueClub.length ||
              filterValueClub.some((club) => woodsClub.includes(club))) && (
              <tr
                className={`${s.stickyStd}`}
                style={{ top: `${tableNavbarHeight + stdRowTop - 10}px` }}
              >
                <td className={`${s.tableStdAndAvg} ${s.tableStd}`} colSpan={4}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      style={{
                        fontFamily: "Manrope",
                        fontSize: "14px",
                        fontWeight: "700",
                        color: "#0E1829",
                      }}
                    >
                      Average (Driver)
                    </Typography>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <Typography
                        style={{
                          fontFamily: "Manrope",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#0E1829",
                        }}
                      >
                        Z Score
                      </Typography>

                      <Tooltip
                        title={
                          <Typography style={{ fontSize: 14, color: "white" }}>
                            The Z-Score measures how many standard deviations
                            this swing is away from your average
                          </Typography>
                        }
                      >
                        <HelpOutlineOutlined
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                            color: "#96A2B5",
                          }}
                        />
                      </Tooltip>
                    </div>
                    {isTourAveragesActive && (
                      <Typography
                        style={{
                          fontFamily: "Manrope",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#445568",
                        }}
                      >
                        Deviation from Tour Avg
                      </Typography>
                    )}
                  </div>
                </td>
                {renderAvgAndStdV3("D")}
              </tr>
            )}
          </tbody>

          <tbody
            onMouseDown={handleTableContainerMouseDown}
            onMouseMove={handleTableContainerMouseMove}
            onMouseLeave={handleTableContainerMouseUp} // In case the mouse leaves the table while dragging
            onMouseUp={handleTableContainerMouseUp}
            style={{ fontSize: "14px" }}
          >
            {table.getRowModel().rows.map((row) => (
              <tr style={{ height: "75px" }} key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  return (
                    <td
                      style={{ textAlign: "start", position: "relative" }}
                      key={cell.id}
                    >
                      {cell.column.id === cell.column.id.toUpperCase() &&
                      !cell.getContext().getValue() ? (
                        <Tooltip
                          title="This swing hasn’t been analyzed yet. Open this 
                          swing in the Sportsbox app to analyze its 3D data"
                        >
                          <p>n/a</p>
                        </Tooltip>
                      ) : (
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )
                      )}

                      <div
                        {...{
                          onDoubleClick: () => cell.column.resetSize(),
                          className: `${s.resizer} ${
                            cell.column.getIsResizing() ? s.isResizing : ""
                          }`,
                        }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        pageNumber={pageNumber}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPage={rowsPerPage}
        showItemsPerPage={data.length > 0}
      />
    </div>
  );
};
